/*
 * TYOHAAR sort dropdown.
 *
 * Wraps the collection's native <select name="sort_by"> without replacing it.
 * Choosing an option writes to the select and dispatches the same bubbling
 * `input` event a real selection fires — which is the event Dawn's
 * <facet-filters-form> is listening for — so sorting, the URL, history and
 * the section re-render all keep working through Dawn's own code.
 */
if (!customElements.get('tyoh-sort')) {
  customElements.define(
    'tyoh-sort',
    class TyohSort extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        this.ui = this.querySelector('.tsort__ui');
        this.button = this.querySelector('[data-sort-button]');
        this.label = this.querySelector('[data-sort-label]');
        this.list = this.querySelector('[data-sort-list]');
        if (!this.select || !this.ui || !this.button || !this.list) return;

        this.options = Array.from(this.list.querySelectorAll('[role="option"]'));

        // The styled control is visible from the first paint; this only marks
        // that it is now interactive, for anything that wants to know.
        this.setAttribute('data-ready', '');

        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

        this.button.addEventListener('click', this.toggle.bind(this));
        this.button.addEventListener('keydown', this.onButtonKeydown.bind(this));
        this.list.addEventListener('click', this.onListClick.bind(this));
        this.list.addEventListener('keydown', this.onListKeydown.bind(this));
        // Keeps the label honest if anything else changes the select.
        this.select.addEventListener('change', this.syncFromSelect.bind(this));

        this.syncFromSelect();
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      }

      get isOpen() {
        return this.button.getAttribute('aria-expanded') === 'true';
      }

      get selectedIndex() {
        const i = this.options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
        return i === -1 ? 0 : i;
      }

      syncFromSelect() {
        const value = this.select.value;
        this.options.forEach((option) => {
          const match = option.dataset.value === value;
          option.setAttribute('aria-selected', match ? 'true' : 'false');
          if (match) this.label.textContent = option.querySelector('.tsort__option-text').textContent.trim();
        });
      }

      open() {
        if (this.isOpen) return;
        this.button.setAttribute('aria-expanded', 'true');
        this.list.hidden = false;
        document.addEventListener('pointerdown', this.onDocumentPointerDown);
        this.focusOption(this.selectedIndex);
      }

      close({ focusButton = false } = {}) {
        if (!this.isOpen) return;
        this.button.setAttribute('aria-expanded', 'false');
        this.list.hidden = true;
        this.list.removeAttribute('aria-activedescendant');
        this.options.forEach((o) => o.classList.remove('is-active'));
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        if (focusButton) this.button.focus();
      }

      toggle() {
        this.isOpen ? this.close({ focusButton: true }) : this.open();
      }

      onDocumentPointerDown(event) {
        if (!this.contains(event.target)) this.close();
      }

      focusOption(index) {
        const clamped = Math.max(0, Math.min(index, this.options.length - 1));
        this.activeIndex = clamped;
        this.options.forEach((o, i) => o.classList.toggle('is-active', i === clamped));
        const option = this.options[clamped];
        if (!option) return;
        this.list.setAttribute('aria-activedescendant', option.id);
        this.list.focus({ preventScroll: true });
        // Keeps the highlighted row in view in a long list without scrolling
        // the page around it.
        const top = option.offsetTop;
        const bottom = top + option.offsetHeight;
        if (top < this.list.scrollTop) this.list.scrollTop = top;
        else if (bottom > this.list.scrollTop + this.list.clientHeight) {
          this.list.scrollTop = bottom - this.list.clientHeight;
        }
      }

      choose(option) {
        if (!option) return;
        const value = option.dataset.value;
        this.close({ focusButton: true });
        if (value === this.select.value) return;

        this.select.value = value;
        this.syncFromSelect();
        // `input` is what facet-filters-form binds to; `change` is dispatched
        // too so anything listening the conventional way also hears it.
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onListClick(event) {
        this.choose(event.target.closest('[role="option"]'));
      }

      onButtonKeydown(event) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
      }

      onListKeydown(event) {
        const last = this.options.length - 1;
        const current = this.activeIndex ?? this.selectedIndex;

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.focusOption(current + 1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.focusOption(current - 1);
            break;
          case 'Home':
            event.preventDefault();
            this.focusOption(0);
            break;
          case 'End':
            event.preventDefault();
            this.focusOption(last);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.choose(this.options[current]);
            break;
          case 'Escape':
            event.preventDefault();
            this.close({ focusButton: true });
            break;
          case 'Tab':
            this.close();
            break;
        }
      }
    }
  );
}

/*
 * The mobile toolbar's Filter button.
 *
 * It opens Dawn's own facets drawer rather than a new one: the drawer, its
 * checkboxes, its Apply and its Clear all already work and already talk to
 * facets.js. All this does is click the <summary> that Dawn hides behind its
 * own opener, which is the same thing Dawn's in-drawer buttons do.
 *
 * Delegated, so cards and toolbars re-rendered by facets.js keep working.
 */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-col-filter-open]');
  if (!opener) return;

  event.preventDefault();
  const summary = document.querySelector('.mobile-facets__wrapper summary');
  if (summary) summary.click();
});

/*
 * Keeping the Filter count live.
 *
 * The badge is rendered by Liquid on page load, but facets.js re-renders only
 * #ProductGridContainer, the .js-filter groups, .active-facets-* and .sorting —
 * never the Filter button. So the number was correct only until the first
 * filter change, and right again only after a reload.
 *
 * Rather than count filters a second time here, it is read from the active
 * pills, which facets.js does keep in step (renderActiveFacets replaces their
 * innerHTML). One source of truth, and it cannot disagree with what is on
 * screen.
 */
(() => {
  const pills = () => document.querySelector('.col-tyo__pills');

  function syncFilterCount() {
    const button = document.querySelector('[data-col-filter-open]');
    const container = pills();
    if (!button || !container) return;

    const count = container.querySelectorAll('facet-remove').length;
    let badge = button.querySelector('.ctb__count');

    if (count === 0) {
      badge?.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'ctb__count';
      button.appendChild(badge);
    }
    badge.textContent = count;
  }

  function watch() {
    const container = pills();
    if (!container) return;
    // The container itself survives the swap, so observing it is enough.
    new MutationObserver(syncFilterCount).observe(container, { childList: true, subtree: true });
    syncFilterCount();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();

/*
 * "Clear all" closes the drawer.
 *
 * Dawn's facet-remove applies the change and leaves the panel open, so after
 * clearing everything the shopper was looking at an empty filter list with an
 * Apply button that had nothing left to apply — the only thing to do was press
 * it anyway, or close the panel by hand.
 *
 * Only the footer's Clear all. The per-group "Clear" links inside an open
 * accordion are a different action: you are narrowing one facet and staying to
 * pick another, and closing the whole panel there would be maddening.
 *
 * Deferred a tick because facet-remove's own handler runs on this same click
 * and starts the fetch; clicking the summary first would tear the panel down
 * underneath it.
 */
document.addEventListener('click', (event) => {
  const clearAll = event.target.closest('.mobile-facets__footer .mobile-facets__clear');
  if (!clearAll) return;

  const wrapper = clearAll.closest('.mobile-facets__wrapper');
  const summary = wrapper?.querySelector('summary');
  if (!summary) return;

  setTimeout(() => {
    // Only if something else has not already closed it.
    if (wrapper.querySelector('details[open]')) summary.click();
  }, 0);
});
